// src/pages/Patientsappointment.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Row, Container, Col, Button, Card, Alert, OverlayTrigger, Tooltip, Table } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import {
  getPatientById,
  getAccountByPatientId,
  getAppointmentById,
} from "../api";
import { Breadcrumb } from '../Components/Breadcrumb/Breadcrumb';
import { getAgedBalancesByAccount } from "../afinanceapi";

import { useEditPatientModal } from "../Components/PatientsAccounts/EditPatients";
import CheckEligibility from "../Components/PatientsAccounts/CheckEligibility";
import { EligibilitySummary } from "../Components/PatientsAccounts/CheckEligibility";
import Session from "../Components/Sessions/Session";
import CaseManagerPanel from "../Components/Cases/CaseModal";
import { useRecordingActions } from "../Components/SyncTo/RecordingProvider";

const EMPTY_REFERRERS = [];

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toISOString().slice(0, 16).replace("T", " ");
};

const formatBreadcrumbDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const ROW = ({ icon, children }) => (
  <div className="mb-1" style={{ lineHeight: "1.25" }}>
    {icon && <i className={`${icon} me-2 text-primary`} />}
    {children}
  </div>
);

function PatientActionsCol({
  patient,
  activeSection,
  isMobile,
  isPortrait,
  setActiveSection,
  setShowCasesPicker,
  openEditPatient,
}) {
  if (isMobile && isPortrait) return null;
  const shortLabels = isMobile && !isPortrait;
  return (
    <Col xs={12} md={2} className="mb-3 mb-md-0">
      <div className="d-flex flex-column gap-1">
        <Button
          variant={activeSection === "info" ? "secondary" : "outline-secondary"}
          className="rounded-0 text-start w-75"
          onClick={() => setActiveSection("info")}
        >
          <i className="fa-solid fa-info me-2" />
          {shortLabels ? "Info" : "Patient Info"}
        </Button>
        <Button
          variant="outline-secondary"
          className="rounded-0 text-start w-75"
          onClick={() => openEditPatient(patient?._id)}
        >
          <i className="fa-solid fa-user-pen me-2" />
          {shortLabels ? "Edit" : "Edit Patient"}
        </Button>
        {patient?.patientMedicalAid && (
          <Button
            variant={activeSection === "eligibility" ? "secondary" : "outline-secondary"}
            className="rounded-0 text-start w-75"
            onClick={() => setActiveSection("eligibility")}
          >
            <i className="fa-solid fa-stethoscope me-2" />
            {shortLabels ? "Check" : "Check Patient"}
          </Button>
        )}
        <Button
          variant={activeSection === "cases" ? "secondary" : "outline-secondary"}
          className="rounded-0 text-start w-75"
          onClick={() => {
            setActiveSection("cases");
            if (isMobile) setShowCasesPicker(true);
          }}
        >
          <i className="fa-solid fa-briefcase-medical me-2" />
          {shortLabels ? "Cases" : "Patient Cases"}
        </Button>
      </div>
    </Col>
  );
}

function PatientInfoCard({
  patient,
  account,
  agedBuckets,
  unallocatedBalance,
  canViewBalance,
  appointmentId,
  appointment,
  id,
  locationState,
  navigate,
}) {
  if (!patient) return <p>Loading patient information...</p>;

  return (
    <>
      {/* Col 2 — Patient details */}
      <Col xs={12} md={3} className="mb-3 mb-md-0">
        {patient?.patientNumber && <ROW icon="fa-regular fa-hashtag">{patient.patientNumber}</ROW>}
        {patient?.patientIdNumber && <ROW icon="fa-solid fa-id-card">{patient.patientIdNumber}</ROW>}
        {patient?.patientCellNumber && <ROW icon="fas fa-phone-alt">{patient.patientCellNumber}</ROW>}
        {patient?.patientEmail && <ROW icon="fas fa-envelope">{patient.patientEmail}</ROW>}
        {patient?.patientGender && (
          <ROW icon="fa-solid fa-venus-mars">
            {patient.patientGender === "1" ? "Male" : patient.patientGender === "2" ? "Female" : "Unspecified"}
          </ROW>
        )}
        {patient?.patientHomeLanguage && <ROW icon="fa fa-language">{patient.patientHomeLanguage}</ROW>}
      </Col>

      {/* Col 3 — Medical aid / IOD */}
      <Col xs={12} md={3} className="mb-3 mb-md-0">
        {patient?.patientIODFund ? (
          <>
            <ROW>{patient.patientIODFund}</ROW>
            {(patient.patientIODPlan || patient.patientIODOption) && (
              <ROW>
                <span className="fw-semibold">{patient.patientIODPlan || "-"}</span>
                {patient.patientIODOption && (
                  <> <span className="text-muted">|</span> <span className="fw-semibold">{patient.patientIODOption}</span></>
                )}
              </ROW>
            )}
            {patient?.patientIODClaimRefNumber && <ROW><strong>Claim Ref #:</strong> {patient.patientIODClaimRefNumber}</ROW>}
            {patient?.patientIODEmployer && <ROW><strong>Employer:</strong> {patient.patientIODEmployer}</ROW>}
            {patient?.patientIODEmployerWCARegNum && <ROW><strong>WCA Reg #:</strong> {patient.patientIODEmployerWCARegNum}</ROW>}
            {patient?.patientIODDateOfInjury && <ROW><strong>Date of Injury:</strong> {patient.patientIODDateOfInjury}</ROW>}
          </>
        ) : patient?.patientMedicalAid ? (
          <>
            <ROW><span className="fw-semibold">{patient.patientMedicalAid}</span></ROW>
            <ROW>
              <span className="fw-semibold">{patient.patientMedicalAidPlan}</span>
              {" "}<span className="text-muted">|</span>{" "}
              <span className="fw-semibold">{patient.patientMedicalAidOption}</span>
            </ROW>
            <ROW>{patient.patientMedicalAidNumber} ({patient.patientMedicalAidDependantCode})</ROW>
            <ROW>
              <span className="text-muted">
                Last checked:{" "}
                {patient?.patientMedicalAidLastCheck?.trim()
                  ? formatDate(patient.patientMedicalAidLastCheck)
                  : "N/A"}
              </span>
            </ROW>
          </>
        ) : null}
      </Col>

      {/* Col 4 — Account info */}
      {account?._id && (
        <Col xs={12} md={4} className="mb-2 mb-md-0 mt-0">
          <div>
            {/* Heading row: ACCOUNT name + arrow */}
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="fw-semibold" style={{ lineHeight: "1.25" }}>
                ACCOUNT:
                <span className="fw-normal ms-2">
                  {account.accountNumber ? `${account.accountNumber} - ` : ""}
                  {[account.accountFullNames, account.accountLastName].filter(Boolean).join(" ")}
                </span>
              </span>
              <OverlayTrigger placement="top" overlay={<Tooltip>Go to Account</Tooltip>}>
                <Button
                  variant="link"
                  className="p-0 text-secondary"
                  style={{ lineHeight: 1 }}
                  onClick={() => {
                    const fi = (patient.patientFullNames || patient.patientFirstName || '').charAt(0);
                    const pShort = `Patient - ${fi ? fi + ' ' : ''}${patient.patientLastName || ''}`.trim();
                    const hasAppointment = appointmentId && appointmentId !== '1' && appointment;
                    navigate(`/accounts/${account._id}`, {
                      state: hasAppointment
                        ? {
                            breadcrumbTrail: [
                              { label: 'Appointments', path: '/bookings' },
                              { label: pShort, path: `/patients/appointment/${id}/${appointmentId}` },
                            ],
                          }
                        : {
                            fromPatient: true,
                            patientName: pShort,
                            patientId: patient._id,
                            ...(locationState?.fromDashboard ? { fromDashboard: true } : {}),
                          },
                    });
                  }}
                >
                  <i className="fa-solid fa-arrow-right fs-6 fw-bold" />
                </Button>
              </OverlayTrigger>
            </div>
            {canViewBalance && agedBuckets && (
              <div className="mt-2" style={{ fontSize: "0.75rem" }}>
                <Table bordered size="sm" className="mb-0">
                  <thead className="table-primary text-center">
                    <tr>
                      <th className="text-primary fw-semibold" style={{width: "20%"}}></th>
                      <th className="text-primary fw-semibold" style={{width: "16%"}}>Outstanding</th>
                      <th className="text-primary fw-semibold" style={{width: "16%"}}>30d</th>
                      <th className="text-primary fw-semibold" style={{width: "16%"}}>60d</th>
                      <th className="text-primary fw-semibold" style={{width: "16%"}}>90d</th>
                      <th className="text-primary fw-semibold" style={{width: "16%"}}>120d+</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-semibold">Patient</td>
                      <td className="text-end text-danger fw-semibold">
                        R {(
                          (agedBuckets.days30?.patient || 0) +
                          (agedBuckets.days60?.patient || 0) +
                          (agedBuckets.days90?.patient || 0) +
                          (agedBuckets.days120plus?.patient || 0)
                        ).toFixed(2)}
                      </td>
                      <td className="text-end">R {(agedBuckets.days30?.patient || 0).toFixed(2)}</td>
                      <td className="text-end">R {(agedBuckets.days60?.patient || 0).toFixed(2)}</td>
                      <td className="text-end">R {(agedBuckets.days90?.patient || 0).toFixed(2)}</td>
                      <td className="text-end">R {(agedBuckets.days120plus?.patient || 0).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold">Medical Aid</td>
                      <td className="text-end text-danger fw-semibold">
                        R {(
                          (agedBuckets.days30?.medicalAid || 0) +
                          (agedBuckets.days60?.medicalAid || 0) +
                          (agedBuckets.days90?.medicalAid || 0) +
                          (agedBuckets.days120plus?.medicalAid || 0)
                        ).toFixed(2)}
                      </td>
                      <td className="text-end">R {(agedBuckets.days30?.medicalAid || 0).toFixed(2)}</td>
                      <td className="text-end">R {(agedBuckets.days60?.medicalAid || 0).toFixed(2)}</td>
                      <td className="text-end">R {(agedBuckets.days90?.medicalAid || 0).toFixed(2)}</td>
                      <td className="text-end">R {(agedBuckets.days120plus?.medicalAid || 0).toFixed(2)}</td>
                    </tr>
                    {unallocatedBalance > 0 && (
                      <tr>
                        <td className="fw-semibold">Unallocated</td>
                        <td className="fw-semibold text-success text-end">R {unallocatedBalance.toFixed(2)}</td>
                        <td colSpan={4}></td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </Col>
      )}
    </>
  );
}

function EligibilityPane({ patient, account, eligibilityData, setEligibilityData }) {
  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="mb-0 text-primary">Eligibility</h6>
          {patient?.patientMedicalAid ? (
            <CheckEligibility
              patient={patient}
              account={account}
              className="w-auto"
              onEligibility={(data) => setEligibilityData(data)}
            />
          ) : null}
        </div>

        {!patient?.patientMedicalAid ? (
          <Alert variant="warning" className="mb-0">
            Patient is not linked to a medical aid.
          </Alert>
        ) : eligibilityData ? (
          <EligibilitySummary eligibility={eligibilityData} />
        ) : (
          <div className="text-muted">No eligibility check performed yet.</div>
        )}
      </Card.Body>
    </Card>
  );
}

function CasesPane({ patient, currentUserId }) {
  return (
    <Card className="border-0">
      <Card.Body>
        <CaseManagerPanel
          patientId={patient?._id}
          existingReferrers={EMPTY_REFERRERS}
          currentUserId={currentUserId}
        />
      </Card.Body>
    </Card>
  );
}

export function Patientsappointment() {
  const { id, appointmentId, sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Stage 4 — auto-surface a ready transcription when the user lands on
  // (or returns to) this patient's page. pollPendingForPatient is idempotent
  // (seenJobIdsRef guard), so re-runs on appointmentId change are safe.
  //
  // The `recording` context value object reference churns on every audio
  // level / timer tick during recording, so we hold it in a ref and only
  // re-fire the effect when the patient id changes — otherwise this would
  // hammer the listJobs endpoint many times per second while recording.
  const recording = useRecordingActions();
  const recordingRef = useRef(null);
  useEffect(() => { recordingRef.current = recording; }, [recording]);
  useEffect(() => {
    if (!id) return;
    recordingRef.current?.pollPendingForPatient?.(id);
  }, [id]);

  const [patient, setPatient] = useState(null);
  const [account, setAccount] = useState({});
  const [appointment, setAppointment] = useState(null);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [activeSection, setActiveSection] = useState("info"); // "info" | "eligibility" | "cases"

  // collapse/expand the patient info content
  const [showPatientInfo, setShowPatientInfo] = useState(!location.state?.collapsed);

  const [isMobile, setIsMobile] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  const [accountBalance, setAccountBalance] = useState(null);
  const [agedBuckets, setAgedBuckets] = useState(null);
  const [unallocatedBalance, setUnallocatedBalance] = useState(0);

  // cases mobile picker toggle
  const [showCasesPicker, setShowCasesPicker] = useState(false);

  // Decode JWT once; derive every role/id flag from this single source.
  const userToken = useMemo(() => {
    try {
      const token = sessionStorage.getItem("user");
      if (!token) return null;
      return jwtDecode(token);
    } catch {
      return null;
    }
  }, []);

  const userRole = useMemo(() => {
    const roles = Array.isArray(userToken?.role) ? userToken.role : [];
    return roles.includes("Practitioner") || roles.includes("SuperAdmin");
  }, [userToken]);

  const canViewBalance = useMemo(() => {
    const roles = Array.isArray(userToken?.role) ? userToken.role : [];
    return roles.includes("Finance") || roles.includes("SuperAdmin");
  }, [userToken]);

  const canAccessPatients = useMemo(() => {
    const roles = [
      ...(Array.isArray(userToken?.role) ? userToken.role : userToken?.role ? [userToken.role] : []),
      ...(Array.isArray(userToken?.permissions) ? userToken.permissions : []),
    ];
    return roles.some(r => ['SuperAdmin', 'Receptionist'].includes(r));
  }, [userToken]);

  const currentUserId = useMemo(() => userToken?._id || "", [userToken]);

  const refreshPatientData = useCallback(async () => {
    try {
      const p = await getPatientById(id);
      setPatient(p);
      if (p?._id) {
        const acc = await getAccountByPatientId(p._id);
        setAccount(acc);
      }
    } catch (e) {
      console.error("Error fetching patient/account:", e);
    }
  }, [id]);

  const { open: openEditPatient, Modal: EditPatientModalHost } =
    useEditPatientModal(refreshPatientData);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const mql = window.matchMedia("(orientation: portrait)");
    const onChange = (e) => setIsPortrait(e.matches ?? mql.matches);
    onChange(mql);
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  useEffect(() => {
    if (id) refreshPatientData();
  }, [id, refreshPatientData]);

  useEffect(() => {
    if (!appointmentId || appointmentId === '1') return;
    let cancelled = false;
    getAppointmentById(appointmentId).then((data) => {
      if (!cancelled) setAppointment(data);
    }).catch((err) => console.error("Failed to load appointment for breadcrumb:", err));
    return () => { cancelled = true; };
  }, [appointmentId]);

  useEffect(() => {
    if (!account?._id || !canViewBalance) return;
    getAgedBalancesByAccount(account._id)
      .then((data) => {
        const buckets = data?.agedBalances || {};
        const total = Object.values(buckets).reduce(
          (sum, b) => sum + (b.patient || 0) + (b.medicalAid || 0),
          0
        );
        setAccountBalance(total);
        setAgedBuckets(buckets);
        setUnallocatedBalance(data?.unallocatedTotal || 0);
      })
      .catch(() => { setAccountBalance(null); setAgedBuckets(null); setUnallocatedBalance(0); });
  }, [account?._id, canViewBalance]);

  const breadcrumbItems = useMemo(() => {
    if (!patient) return null;
    const firstInitial = (patient.patientFullNames || patient.patientFirstName || '').charAt(0);
    const shortName = `Patient - ${firstInitial ? firstInitial + ' ' : ''}${patient.patientLastName || ''}`.trim();
    const apptShortName = `${firstInitial ? firstInitial + ' ' : ''}${patient.patientLastName || ''}`.trim();

    if (sessionId) {
      // PAGE-08: Session Notes — Appointments > [Date] - [Patient Name] > Session Notes
      if (!appointment) return null;
      const appointmentLabel = `${formatBreadcrumbDate(appointment.start)} \u2013 ${apptShortName}`;
      return [
        { label: 'Appointments', path: '/bookings' },
        { label: appointmentLabel, path: `/patients/appointment/${id}/${appointment._id || appointmentId}` },
        { label: 'Session Notes' },
      ];
    }

    if (appointmentId && appointmentId !== '1') {
      // PAGE-07: Appointment Detail — Appointments > [Date] - [Patient Name]
      if (!appointment) return null;
      const appointmentLabel = `${formatBreadcrumbDate(appointment.start)} \u2013 ${apptShortName}`;
      return [
        { label: 'Appointments', path: '/bookings' },
        { label: appointmentLabel },
      ];
    }

    // PAGE-01: Patient Profile — upstream trail > [Patient Name]
    if (location.state?.breadcrumbTrail?.length) {
      return [...location.state.breadcrumbTrail, { label: shortName }];
    }
    if (location.state?.fromDashboard) {
      return [
        { label: 'Dashboard', path: '/home' },
        { label: shortName },
      ];
    }
    return [
      { label: 'Patients', path: '/patients', canAccess: canAccessPatients },
      { label: shortName },
    ];
  }, [patient, appointment, id, appointmentId, sessionId, canAccessPatients, location.state]);

  const renderRightPane = () => {
    return (
      <Row className="pt-1 pb-2 align-items-start gx-2" style={{ fontSize: "0.875rem" }}>
        <PatientActionsCol
          patient={patient}
          activeSection={activeSection}
          isMobile={isMobile}
          isPortrait={isPortrait}
          setActiveSection={setActiveSection}
          setShowCasesPicker={setShowCasesPicker}
          openEditPatient={openEditPatient}
        />
        {activeSection === "info" && (
          <PatientInfoCard
            patient={patient}
            account={account}
            agedBuckets={agedBuckets}
            unallocatedBalance={unallocatedBalance}
            canViewBalance={canViewBalance}
            appointmentId={appointmentId}
            appointment={appointment}
            id={id}
            locationState={location.state}
            navigate={navigate}
          />
        )}
        {activeSection === "eligibility" && (
          <Col xs={12} md={10}>
            <EligibilityPane
              patient={patient}
              account={account}
              eligibilityData={eligibilityData}
              setEligibilityData={setEligibilityData}
            />
          </Col>
        )}
        {activeSection === "cases" && (
          <Col xs={12} md={10}>
            <CasesPane patient={patient} currentUserId={currentUserId} />
          </Col>
        )}
      </Row>
    );
  };

  return (
    <div className="ms-1" style={{ marginTop: "65px" }}>
      <Container fluid>
        {breadcrumbItems && <Breadcrumb items={breadcrumbItems} />}
        {/* Patient name header */}
        <div className="px-2 py-2 mb-2">
          <h5 className={`text-primary fw-semibold mb-0 ${!showPatientInfo ? "text-center" : ""}`}>
            {patient ? (
              <>
                {`${patient.patientTitle || ""} ${patient.patientFullNames || ""} ${patient.patientLastName || ""}`.trim()}
                {patient.patientIODFund ? (
                  (patient.patientIODFundCategory || "").toUpperCase() === "INSURANCE" ? (
                    <sup><span className="badge bg-info text-dark ms-2" title="Insurance" style={{ fontSize: "0.55rem", verticalAlign: "super" }}>I</span></sup>
                  ) : (
                    <sup><span className="badge bg-warning text-dark ms-2" title="IOD" style={{ fontSize: "0.55rem", verticalAlign: "super" }}>I</span></sup>
                  )
                ) : patient.patientMedicalAid ? (
                  <sup><span className="badge bg-primary ms-2" title="Medical Aid" style={{ fontSize: "0.55rem", verticalAlign: "super" }}>M</span></sup>
                ) : (
                  <sup><span className="badge bg-secondary ms-2" title="Private" style={{ fontSize: "0.55rem", verticalAlign: "super" }}>P</span></sup>
                )}
                {patient.patientMedicalAid && (
                  <span
                    className={`ms-1 ${(patient.patientMedicalAidStatus || "").toLowerCase().includes("active") ? "text-success" : "text-danger"}`}
                    title={`${patient.patientMedicalAidStatus || "Unknown"}${patient.patientMedicalAidJoinDate ? ` | Joined: ${patient.patientMedicalAidJoinDate}` : ""}`}
                    style={{ fontSize: "0.55rem", verticalAlign: "super", cursor: "help" }}
                  >
                    <i className="fa-duotone fa-light fa-wave-pulse fs-6"></i>
                  </span>
                )}
              </>
            ) : "Loading..."}
          </h5>
        </div>

        {/* Full-width expandable content */}
        {showPatientInfo && (
          <div className="px-2 pb-2">
            {renderRightPane()}
          </div>
        )}
      </Container>

      {/* Divider with expand/collapse arrow centered on it */}
      <div className="position-relative" style={{ margin: "4px 0 0 0" }}>
        <hr className="m-0" />
        <div
          role="button"
          aria-label={showPatientInfo ? "Collapse patient info" : "Expand patient info"}
          onClick={() => setShowPatientInfo((s) => !s)}
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            background: showPatientInfo ? "white" : "#cfe2ff",
            border: `1px solid ${showPatientInfo ? "#dee2e6" : "#0d6efd"}`,
            borderRadius: "50%",
            width: "22px",
            height: "22px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: showPatientInfo ? "#6c757d" : "#0d6efd",
            fontSize: "10px",
            zIndex: 1,
            userSelect: "none",
          }}
        >
          <i className={`fa-solid fa-chevron-${showPatientInfo ? "up" : "down"}`} />
        </div>
      </div>

      {/* Sessions area */}
      <Row className="ms-1 me-2 pt-3 pt-md-0">
        {userRole && (
          <Session
            patient={patient}
            appointment={appointment}
            extraMobileMenu={[
              {
                key: "patient-info",
                label: "Patient Info",
                onClick: () => { setActiveSection("info"); setShowPatientInfo(true); },
              },
              {
                key: "edit-patient",
                label: "Edit Patient",
                onClick: () => { openEditPatient(patient?._id); },
              },
              ...(patient?.patientMedicalAid
                ? [
                    {
                      key: "check-patient",
                      label: "Check Patient",
                      onClick: () => { setActiveSection("eligibility"); setShowPatientInfo(true); },
                    },
                  ]
                : []),
              {
                key: "cases",
                label: "Patient Cases",
                onClick: () => {
                  setActiveSection("cases");
                  setShowPatientInfo(true);
                  setShowCasesPicker(true);
                },
              },
            ]}
            onMobileNavigate={(where) => {
              if (where === "patient") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          />
        )}
      </Row>

      <EditPatientModalHost />
    </div>
  );
}
